/**
 * Portfolio 文件选择 · 平台策略解析
 *
 * 三端 WebView / 系统选择器行为不同，禁止在 pick 层用大量 `if (platform)` 耦合。
 * 本模块只负责「当前环境应走哪条策略」，具体实现见：
 * - iOS/Web：`pickPortfolioFilesWithFocusFallback`（detached input）
 * - Android：`PortfolioFilePickerHost` + `pickPortfolioFilesFromMountedInput`（session）
 *
 * @see apps/token-seek/features/20260610-portfolio-file-pick-platform-strategy/SPEC.md
 */
import { resolvePortfolioInAppPlatformFlags } from "./portfolioInAppPlatform";

/** 文件选择实现策略，与 `pickPortfolioFiles.ts` 分发一一对应 */
export type PortfolioFilePickStrategy = "ios" | "android" | "web";

/**
 * 根据平台 flag 解析策略（可单测，不依赖 bridge mock）。
 * Android 优先于 iOS，避免双 flag 异常时误走 iOS 路径。
 */
export function resolvePortfolioFilePickStrategyFromFlags(flags: {
  isIosInApp?: boolean;
  isAndroidInApp?: boolean;
}): PortfolioFilePickStrategy {
  const { isIosInApp, isAndroidInApp } = flags;
  if (isAndroidInApp) {
    return "android";
  }
  if (isIosInApp) {
    return "ios";
  }
  return "web";
}

/** 运行时策略：读取 bridge 平台后委托 {@link resolvePortfolioFilePickStrategyFromFlags} */
export function resolvePortfolioFilePickStrategy(): PortfolioFilePickStrategy {
  return resolvePortfolioFilePickStrategyFromFlags(
    resolvePortfolioInAppPlatformFlags(),
  );
}

/**
 * 开弹窗前选图是否使用根节点 `PortfolioFilePickerHost`。
 * 仅 Android：mounted input 在 WebView 中比 detached 更稳定。
 */
export function shouldUsePortfolioPickerHost(
  strategy: PortfolioFilePickStrategy,
): boolean {
  return strategy === "android";
}

/**
 * 弹窗内 +Upload 是否走 `runPortfolioFilePickSession`。
 * 仅 Android：需在 session 内先 stabilize 再清 input，避免 blob 失效。
 * iOS/Web 使用 Sender 式 click + onChange。
 */
export function shouldUsePortfolioPickSessionForMore(
  strategy: PortfolioFilePickStrategy,
): boolean {
  return strategy === "android";
}
